import { Link, useLocation } from 'react-router-dom'
import { Bars3Icon, XMarkIcon, UserIcon, ChevronDownIcon } from '@heroicons/react/24/outline'
import { useState, useEffect } from 'react'
import { useAuthStore } from '../store/authStore'
import ChangePasswordModal from './auth/ChangePasswordModal'
import { UserRole } from '../types/auth'
import { isEaseTheme } from '@/config/designSystem'

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false)
  const location = useLocation()
  const { isAuthenticated, user, logout } = useAuthStore()
  const ease = isEaseTheme()

  const navigation = [
    { name: 'Trang chủ', href: '/' },
    { name: 'Khóa học', href: '/courses' },
    { name: 'Workshop', href: '/workshop' },
    { name: 'Thư viện', href: '/skills' },
    { name: 'Blog', href: '/blog' },
    { name: 'About', href: '/about' },
  ]

  const myCoursesLink = { name: 'Khóa học của tôi', href: '/student/my-courses' }
  const myHomeworksLink = { name: 'BTVN của tôi', href: '/student/homeworks' }
  const myWorkshopsLink = { name: 'Thư viện Workshop', href: '/workshops' }
  const isAdmin = Boolean(user?.roles?.includes(UserRole.ADMIN))

  useEffect(() => {
    setMobileMenuOpen(false)
    setUserMenuOpen(false)
  }, [location])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuOpen && !(event.target as Element).closest('.relative')) {
        setUserMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [userMenuOpen])

  const isActiveLink = (href: string) => {
    if (href.includes('#')) return false
    if (href === '/') return location.pathname === '/'
    return location.pathname.startsWith(href)
  }

  const NavItem = ({
    item,
    className,
    onNavigate,
  }: {
    item: { name: string; href: string }
    className: string
    onNavigate?: () => void
  }) => {
    if (item.href.includes('#')) {
      return (
        <a href={item.href} className={className} onClick={() => onNavigate?.()}>
          {item.name}
        </a>
      )
    }
    return (
      <Link to={item.href} className={className} onClick={() => onNavigate?.()}>
        {item.name}
      </Link>
    )
  }

  if (ease) {
    return (
      <header className="sticky top-0 z-50 border-b border-eh-border bg-eh-cream/90 backdrop-blur-[12px] text-eh-charcoal">
        <nav className="eh-container" aria-label="Main navigation">
          <div className="grid grid-cols-[1fr_auto] items-center gap-4 py-4 lg:grid-cols-[1fr_auto_1fr]">
            <Link
              to="/"
              className="inline-flex w-fit items-center gap-3"
              aria-label="M2MBA - Rèn tư duy sâu, tạo giá trị thật - Trang chủ"
            >
              <img
                src="/brand-m2mba-logo.png"
                alt="M2MBA"
                width={231}
                height={206}
                className="h-10 w-auto md:h-12"
              />
              <span className="hidden items-center gap-3 lg:flex">
                <span className="h-8 w-px bg-eh-border" aria-hidden />
                <span className="whitespace-nowrap font-display text-sm font-semibold tracking-[-0.01em] text-eh-forest-ink">
                  Rèn tư duy sâu · Tạo giá trị thật
                </span>
              </span>
            </Link>

            <div className="hidden items-center gap-1.5 md:flex">
              {navigation.map((item) => {
                const isActive = isActiveLink(item.href)
                return (
                  <NavItem
                    key={item.name}
                    item={item}
                    className={`rounded-eh-sm px-3.5 py-2 text-sm transition-colors ${
                      isActive
                        ? 'bg-eh-keylime text-eh-forest-ink font-medium'
                        : 'text-eh-charcoal hover:bg-eh-keylime hover:text-eh-forest-ink'
                    }`}
                  />
                )
              })}
            </div>

            <div className="hidden items-center justify-end gap-3 md:flex">
              {isAuthenticated && user ? (
                <div className="relative">
                  <button
                    type="button"
                    className="flex items-center rounded-full text-sm text-eh-forest-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-eh-forest-ink"
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                  >
                    <UserIcon className="mr-1 h-6 w-6" />
                    <span className="hidden lg:block">{user.firstName || user.fullName}</span>
                    <ChevronDownIcon className="ml-1 h-4 w-4" />
                  </button>
                  {userMenuOpen && (
                    <div className="absolute right-0 z-50 mt-2 w-56 origin-top-right rounded-eh border border-eh-border bg-eh-cream py-1 shadow-eh-soft">
                      <div className="px-4 py-2 text-xs text-eh-muted">Đăng nhập bởi</div>
                      <div className="px-4 py-1 text-sm font-medium text-eh-forest-ink">{user.fullName}</div>
                      <div className="border-b border-eh-border px-4 py-1 text-xs text-eh-muted">
                        {user.email}
                      </div>
                      <Link
                        to={myCoursesLink.href}
                        className="block px-4 py-2 text-sm hover:bg-eh-keylime"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        {myCoursesLink.name}
                      </Link>
                      <Link
                        to={myHomeworksLink.href}
                        className="block px-4 py-2 text-sm hover:bg-eh-keylime"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        {myHomeworksLink.name}
                      </Link>
                      <Link
                        to={myWorkshopsLink.href}
                        className="block px-4 py-2 text-sm hover:bg-eh-keylime"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        {myWorkshopsLink.name}
                      </Link>
                      <button
                        onClick={() => {
                          setShowChangePasswordModal(true)
                          setUserMenuOpen(false)
                        }}
                        className="block w-full px-4 py-2 text-left text-sm hover:bg-eh-keylime"
                      >
                        Đổi mật khẩu
                      </button>
                      {isAdmin && (
                        <Link
                          to="/admin/courses"
                          className="block px-4 py-2 text-sm hover:bg-eh-keylime"
                          onClick={() => setUserMenuOpen(false)}
                        >
                          Khu vực Admin
                        </Link>
                      )}
                      <button
                        onClick={() => {
                          logout()
                          setUserMenuOpen(false)
                        }}
                        className="block w-full border-t border-eh-border px-4 py-2 text-left text-sm hover:bg-eh-keylime"
                      >
                        Đăng xuất
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <Link to="/login" className="text-sm text-eh-forest-ink">
                    Đăng nhập
                  </Link>
                  <Link to="/register" className="eh-btn !min-h-[44px] !px-4 !py-2 text-sm">
                    Đăng ký <span className="eh-arrow">→</span>
                  </Link>
                </>
              )}
            </div>

            <div className="flex items-center justify-end md:hidden">
              <button
                type="button"
                className="grid h-11 w-11 place-items-center rounded-eh-sm border border-eh-border text-eh-forest-ink"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle mobile menu"
                aria-expanded={mobileMenuOpen}
              >
                {mobileMenuOpen ? <XMarkIcon className="h-6 w-6" /> : <Bars3Icon className="h-6 w-6" />}
              </button>
            </div>
          </div>

          {mobileMenuOpen && (
            <div className="grid gap-1.5 border-t border-eh-border pb-4 pt-3 md:hidden">
              {navigation.map((item) => (
                <NavItem
                  key={item.name}
                  item={item}
                  onNavigate={() => setMobileMenuOpen(false)}
                  className={`block rounded-eh-sm px-3.5 py-3.5 text-base ${
                    isActiveLink(item.href) ? 'bg-eh-keylime font-medium' : 'bg-eh-keylime/50'
                  }`}
                />
              ))}
              <div className="mt-2 space-y-1 border-t border-eh-border pt-3">
                {isAuthenticated && user ? (
                  <>
                    <div className="px-3 py-2">
                      <div className="font-medium text-eh-forest-ink">{user.fullName}</div>
                      <div className="text-sm text-eh-muted">{user.email}</div>
                    </div>
                    <Link
                      to={myCoursesLink.href}
                      className="block rounded-eh-sm px-3 py-2.5 hover:bg-eh-keylime"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {myCoursesLink.name}
                    </Link>
                    <Link
                      to={myHomeworksLink.href}
                      className="block rounded-eh-sm px-3 py-2.5 hover:bg-eh-keylime"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {myHomeworksLink.name}
                    </Link>
                    <Link
                      to={myWorkshopsLink.href}
                      className="block rounded-eh-sm px-3 py-2.5 hover:bg-eh-keylime"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {myWorkshopsLink.name}
                    </Link>
                    {isAdmin && (
                      <Link
                        to="/admin/courses"
                        className="block rounded-eh-sm px-3 py-2.5 hover:bg-eh-keylime"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Khu vực Admin
                      </Link>
                    )}
                    <button
                      onClick={() => {
                        logout()
                        setMobileMenuOpen(false)
                      }}
                      className="block w-full rounded-eh-sm px-3 py-2.5 text-left hover:bg-eh-keylime"
                    >
                      Đăng xuất
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/login"
                      className="block rounded-eh-sm px-3 py-2.5 hover:bg-eh-keylime"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Đăng nhập
                    </Link>
                    <Link
                      to="/register"
                      className="block rounded-eh-sm px-3 py-2.5 font-medium text-eh-forest-ink hover:bg-eh-keylime"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Đăng ký
                    </Link>
                  </>
                )}
              </div>
            </div>
          )}
        </nav>

        <ChangePasswordModal
          isOpen={showChangePasswordModal}
          onClose={() => setShowChangePasswordModal(false)}
        />
      </header>
    )
  }

  // Legacy header (dark Structured Innovation)
  const muted = 'text-m2-on-surface-variant hover:text-m2-on-surface'
  const menuItem = 'text-m2-on-surface hover:bg-m2-surface-high'
  const mobileItem = 'text-m2-on-surface-variant hover:text-m2-on-surface hover:bg-m2-surface-high'

  return (
    <header className="sticky top-0 z-50 border-b border-m2-outline-variant bg-m2-surface/80 backdrop-blur-[12px] text-m2-on-surface">
      <nav className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 justify-between">
          <div className="flex">
            <div className="flex flex-shrink-0 items-center">
              <Link
                to="/"
                className="inline-flex items-center transition-opacity hover:opacity-90"
                aria-label="M2MBA - Trang chủ"
              >
                <img
                  src="/brand-m2mba-logo.png"
                  alt="M2MBA"
                  width={231}
                  height={206}
                  className="h-10 w-auto"
                />
              </Link>
            </div>
            <div className="hidden sm:ml-6 sm:flex sm:space-x-6 lg:space-x-8">
              {navigation.map((item) => {
                const isActive = isActiveLink(item.href)
                return (
                  <NavItem
                    key={item.name}
                    item={item}
                    className={`${
                      isActive
                        ? 'border-m2-primary text-m2-on-surface'
                        : 'border-transparent text-m2-on-surface-variant hover:border-m2-outline hover:text-m2-on-surface'
                    } inline-flex items-center border-b-2 px-1 pt-1 text-sm font-medium transition-colors`}
                  />
                )
              })}
            </div>
          </div>

          <div className="hidden space-x-4 sm:ml-6 sm:flex sm:items-center">
            {isAuthenticated && user ? (
              <div className="relative">
                <button
                  type="button"
                  className={`flex items-center rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-m2-primary focus:ring-offset-2 focus:ring-offset-m2-surface ${muted}`}
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                >
                  <UserIcon className="mr-1 h-6 w-6" />
                  <span className="hidden lg:block">{user.firstName || user.fullName}</span>
                  <ChevronDownIcon className="ml-1 h-4 w-4" />
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 z-50 mt-2 w-56 origin-top-right rounded-lg bg-m2-surface-container py-1 shadow-lg ring-1 ring-m2-outline-variant focus:outline-none">
                    <div className="px-4 py-2 text-xs text-m2-on-surface-variant">Đăng nhập bởi</div>
                    <div className="px-4 py-1 text-sm font-medium text-m2-on-surface">{user.fullName}</div>
                    <div className="border-b border-m2-outline-variant px-4 py-1 text-xs text-m2-on-surface-variant">
                      {user.email}
                    </div>
                    <Link to={myCoursesLink.href} className={`block px-4 py-2 text-sm ${menuItem}`} onClick={() => setUserMenuOpen(false)}>
                      {myCoursesLink.name}
                    </Link>
                    <Link to={myHomeworksLink.href} className={`block px-4 py-2 text-sm ${menuItem}`} onClick={() => setUserMenuOpen(false)}>
                      {myHomeworksLink.name}
                    </Link>
                    <Link to={myWorkshopsLink.href} className={`block px-4 py-2 text-sm ${menuItem}`} onClick={() => setUserMenuOpen(false)}>
                      {myWorkshopsLink.name}
                    </Link>
                    <button
                      onClick={() => {
                        setShowChangePasswordModal(true)
                        setUserMenuOpen(false)
                      }}
                      className={`block w-full px-4 py-2 text-left text-sm ${menuItem}`}
                    >
                      Đổi mật khẩu
                    </button>
                    {isAdmin && (
                      <Link to="/admin/courses" className={`block px-4 py-2 text-sm ${menuItem}`} onClick={() => setUserMenuOpen(false)}>
                        Khu vực Admin
                      </Link>
                    )}
                    <button
                      onClick={() => {
                        logout()
                        setUserMenuOpen(false)
                      }}
                      className={`block w-full border-t border-m2-outline-variant px-4 py-2 text-left text-sm ${menuItem}`}
                    >
                      Đăng xuất
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link to="/login" className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${muted}`}>
                  Đăng nhập
                </Link>
                <Link to="/register" className="btn-primary">
                  Đăng ký
                </Link>
              </>
            )}
          </div>

          <div className="flex items-center sm:hidden">
            <button
              type="button"
              className={`rounded-md p-2 transition-colors ${muted}`}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle mobile menu"
            >
              {mobileMenuOpen ? <XMarkIcon className="h-6 w-6" /> : <Bars3Icon className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="border-t border-m2-outline-variant sm:hidden">
            <div className="space-y-1 pb-3 pt-2">
              {navigation.map((item) => (
                <NavItem
                  key={item.name}
                  item={item}
                  onNavigate={() => setMobileMenuOpen(false)}
                  className={`${
                    isActiveLink(item.href) ? 'bg-m2-primary/10 text-m2-primary' : mobileItem
                  } block rounded-md px-3 py-2 text-base font-medium transition-colors`}
                />
              ))}
              {isAuthenticated && (
                <>
                  <Link
                    to={myCoursesLink.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`${
                      isActiveLink(myCoursesLink.href) ? 'bg-m2-primary/10 text-m2-primary' : mobileItem
                    } block rounded-md px-3 py-2 text-base font-medium transition-colors`}
                  >
                    {myCoursesLink.name}
                  </Link>
                  <Link
                    to={myHomeworksLink.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`${
                      isActiveLink(myHomeworksLink.href) ? 'bg-m2-primary/10 text-m2-primary' : mobileItem
                    } block rounded-md px-3 py-2 text-base font-medium transition-colors`}
                  >
                    {myHomeworksLink.name}
                  </Link>
                  <Link
                    to={myWorkshopsLink.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`${
                      isActiveLink(myWorkshopsLink.href) ? 'bg-m2-primary/10 text-m2-primary' : mobileItem
                    } block rounded-md px-3 py-2 text-base font-medium transition-colors`}
                  >
                    {myWorkshopsLink.name}
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </nav>

      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
      />
    </header>
  )
}

export default Header
